import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { AlertTriangle } from 'lucide-react';
import type { BatchDetail } from '@autopilot/contracts';
import { KpiCard } from '../../components/KpiCard';
import { DistributionChart } from '../../components/DistributionChart';
import { useNameLookup } from '../../api/hooks';
import { formatDateTime } from '../../lib/format';

export function SelectionProgress({ onCancel, cancelling, canCancel }: { onCancel: () => void; cancelling: boolean; canCancel: boolean }) {
  return (
    <Card component="section" aria-labelledby="sel-progress">
      <CardContent sx={{ p: 3 }}>
        <Typography id="sel-progress" variant="h5" component="h2">
          Tickets werden geladen
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 2 }}>
          Nidus erstellt die vollständige Auswahl und prüft die Negativliste sowie die Lesbarkeit von Anhängen – ohne Modellaufrufe und ohne Änderungen an den Vorgängen. Sie können die Seite verlassen; der Stand bleibt erhalten.
        </Typography>
        <LinearProgress aria-label="Auswahl wird erstellt" />
        {canCancel && (
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={onCancel} disabled={cancelling}>
              Auswahl abbrechen
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export function SelectionPreview({ batch }: { batch: BatchDetail }) {
  const names = useNameLookup();
  const c = batch.selection?.counts;
  if (!c) return null;
  const bars = c.byType.map((t) => ({
    key: t.vorgangstypId ?? 'none',
    label: t.vorgangstypId ? names.typen.get(t.vorgangstypId) ?? 'Unbekannter Typ' : 'Ohne Typ',
    count: t.count,
    muted: t.vorgangstypId === null,
  }));
  if (!bars.some((b) => b.key === 'none')) bars.push({ key: 'none', label: 'Ohne Typ', count: 0, muted: true });
  return (
    <Box component="section" aria-labelledby="preview-title">
      <Typography id="preview-title" variant="h5" component="h2" sx={{ mb: 1.5 }}>
        Auswahl
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' } }}>
        <KpiCard label="Nutzbare Tickets" value={c.usable.toLocaleString('de-DE')} hint={`von ${c.raw.toLocaleString('de-DE')} berechtigten Tickets`} testId="kpi-usable" />
        <KpiCard
          label="Betroffene Liegenschaften"
          value={c.distinctObjects.toLocaleString('de-DE')}
          hint={`Ohne Liegenschaft: ${c.withoutObject.toLocaleString('de-DE')} Tickets`}
          testId="kpi-objects"
        />
        <KpiCard
          label="Ausgeschlossene Tickets"
          value={(c.excludedByNegativeList + c.incomplete).toLocaleString('de-DE')}
          hint={
            <>
              Negativliste: {c.excludedByNegativeList} · Prüfung unvollständig: {c.incomplete}
            </>
          }
          testId="kpi-excluded"
        />
        <KpiCard
          label="Stand der Auswahl"
          value={<Typography component="span" variant="h6">{formatDateTime(batch.selection?.selectionAsOf)}</Typography>}
          hint="Später hinzukommende Tickets werden nicht still aufgenommen."
        />
      </Box>
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <DistributionChart
            title="Typenverteilung der nutzbaren Tickets"
            bars={bars}
            total={c.usable}
            orientation="horizontal"
            testId="type-distribution"
            tableCaption="Typenverteilung als Tabelle"
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
            Alle Zahlen beziehen sich auf denselben nutzbaren Bestand. Liegenschaften = verschiedene Liegenschaften; Tickets ohne Liegenschaft werden separat gezählt.
          </Typography>
        </CardContent>
      </Card>
      {c.usable === 0 && (
        <Alert severity="warning" icon={<AlertTriangle size={20} />} sx={{ mt: 2 }}>
          Es gibt keine nutzbaren Tickets. Pre-Heating ist nicht möglich – passen Sie die Filter an und laden Sie die Tickets erneut.
        </Alert>
      )}
    </Box>
  );
}
